import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminEventChatBroadcastInput, AdminEventChatBroadcastStatusDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../../app/api-client';
import { useAuth } from '../../auth/auth-context';

export function EventBroadcastCard({ eventId, eventStatus }: { eventId: string; eventStatus: string }) {
  const { adminRoles } = useAuth();
  const qc = useQueryClient();
  const canUse = adminRoles.includes('SUPERADMIN');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);

  const statusQuery = useQuery({
    queryKey: ['admin', 'events', eventId, 'chat-broadcast-status'],
    queryFn: () =>
      api.get(`admin/events/${eventId}/chat-broadcast/status`).json<AdminEventChatBroadcastStatusDto>(),
    enabled: canUse && eventStatus === 'PUBLISHED',
  });

  const send = useMutation({
    mutationFn: (body: AdminEventChatBroadcastInput) =>
      api.post(`admin/events/${eventId}/chat-broadcast`, { json: body }).json<{ broadcastId: string }>(),
    onSuccess: () => {
      toast.success('Пост поставлен в очередь отправки в чат');
      void qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'chat-broadcast-status'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Не удалось отправить'),
  });

  if (!canUse || eventStatus !== 'PUBLISHED') return null;

  const chatTitle = statusQuery.data?.chatTitle;
  const alreadySent = statusQuery.data?.alreadySent ?? false;

  const runSend = (forceResend: boolean) => {
    send.mutate({ forceResend });
    setConfirmOpen(false);
    setResendOpen(false);
  };

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div>
        <h3 className="font-semibold">Автопостинг в Telegram</h3>
        <p className="text-sm text-muted-foreground">
          {chatTitle ? `Чат города: ${chatTitle}` : 'У города не привязан чат — настройте в карточке города.'}
        </p>
      </div>
      <Button
        type="button"
        variant="secondary"
        disabled={!chatTitle || send.isPending}
        onClick={() => (alreadySent ? setResendOpen(true) : setConfirmOpen(true))}
      >
        Автопостинг
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отправить в чат?</AlertDialogTitle>
            <AlertDialogDescription>
              {chatTitle
                ? `Вы уверены, что хотите разместить в «${chatTitle}» это мероприятие?`
                : 'Чат не привязан.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={() => runSend(false)}>Отправить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resendOpen} onOpenChange={setResendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отправить повторно?</AlertDialogTitle>
            <AlertDialogDescription>
              Сообщение уже размещалось в чате. Вы точно хотите отправить его снова?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={() => runSend(true)}>Отправить повторно</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
